import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";

function ItemList({ endpoint, testidPrefix }) {
  const { isReadOnly } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [pageItems, ctrl] = usePagination(items, 10);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = useCallback(async () => setItems((await api.get(endpoint)).data), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoint, form);
      setForm({ name: "", description: "" });
      toast.success("Tersimpan");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Terhapus");
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      return false;
    }
  };
  const doConfirmDel = async () => {
    if (!confirmDel) return;
    if (await remove(confirmDel.id)) setConfirmDel(null);
  };

  return (
    <div className="space-y-6">
      {!isReadOnly && (
      <form onSubmit={submit} className="card-tech p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="label-mini">Nama</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${testidPrefix}-name-input`} />
        </div>
        <div className="md:col-span-2">
          <Label className="label-mini">Deskripsi</Label>
          <Textarea rows={1} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`${testidPrefix}-desc-input`} />
        </div>
        <div className="md:col-span-3">
          <Button type="submit" className="rounded-sm" data-testid={`${testidPrefix}-add-button`}><Plus className="h-4 w-4 mr-2" /> Tambah</Button>
        </div>
      </form>
      )}

      <div className="card-tech p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-6 py-3">Nama</th>
              <th className="text-left px-6 py-3">Deskripsi</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-muted-foreground">Belum ada data.</td></tr>}
            {pageItems.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`${testidPrefix}-row-${it.id}`}>
                <td className="px-6 py-3 font-medium">{it.name}</td>
                <td className="px-6 py-3 text-muted-foreground">{it.description}</td>
                <td className="px-6 py-3 text-right">
                  {!isReadOnly && (
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(it)} data-testid={`${testidPrefix}-delete-${it.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination ctrl={ctrl} testidPrefix={`${testidPrefix}-pg`} />
      </div>
      <ConfirmDelete
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doConfirmDel}
        entityLabel={confirmDel?.name}
        testIdPrefix={`confirm-delete-${testidPrefix}`}
      />
    </div>
  );
}

export default function Templates() {
  return (
    <div className="space-y-8" data-testid="templates-page">
      <div>
        <div className="label-mini">Template Activity</div>
        <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Template & Task</h1>
        <p className="text-sm text-muted-foreground mt-2">Simpan template task & activity untuk digunakan kembali.</p>
      </div>
      <Tabs defaultValue="tasks">
        <TabsList data-testid="template-tabs">
          <TabsTrigger value="tasks" data-testid="tab-task-templates">Task Templates</TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activity-templates">Activity Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="tasks" className="pt-6">
          <ItemList endpoint="/task-templates" testidPrefix="task-tpl" />
        </TabsContent>
        <TabsContent value="activities" className="pt-6">
          <ItemList endpoint="/template-activities" testidPrefix="activity-tpl" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
